"use client";
/**
 * LandingNav — the marketing top navigation extracted from the landing page
 * (components/marketing/svendetid-landing/SvendetidLanding.tsx) so /features
 * and /pricing share one source of truth.
 *
 * Self-contained: owns its scroll/dropdown/mobile state and ships the CSS vars
 * and classes it depends on (--green-dark, --ink, .nav-link, .btn-primary,
 * .features-dropdown, mobile panel, responsive rules). On the landing page the
 * same markup is inlined and relies on that page's <style> block; here we scope
 * an identical block so the nav renders the same anywhere it is used.
 */
import { useState, useEffect, useRef } from "react";
import { Sora, DM_Sans, DM_Serif_Display } from "next/font/google";
import { SignInButton, SignUpButton, useAuth, useOrganization } from "@clerk/nextjs";
import { useRouter } from "next/navigation";
import Link from "next/link";

// Fonts via next/font (self-hosted, preloaded, non render-blocking) — replaces
// the former font import inside NAV_CSS. This nav is shared across every
// marketing page, so loading its fonts here means each page no longer pays a
// render-blocking round-trip to Google for the nav wordmark.
const navSans = DM_Sans({ subsets: ["latin"], weight: ["400", "500", "600", "700"], display: "swap", variable: "--font-sans" });
const navDisplay = Sora({ subsets: ["latin"], weight: ["300", "400", "600", "700", "800"], display: "swap", variable: "--font-display" });
const navSerifDisplay = DM_Serif_Display({ subsets: ["latin"], weight: "400", style: ["normal", "italic"], display: "swap", variable: "--font-serif-display" });
const navFontClass = `${navSans.variable} ${navDisplay.variable} ${navSerifDisplay.variable}`;

const NAV_CSS = `
.landing-nav-scope {
  --green-dark: #1B4332;
  --blue-dark: #0000FF;
  --green-mid: #2D6A4F;
  --green-light: #52B788;
  --green-pale: #D8F3DC;
  --ink: #1A1A1A;
  --muted: #6B6B6B;
  --border: #D9D5CC;
  font-family: var(--font-display), var(--font-sans), sans-serif;
}
.landing-nav-scope .nav-link {
  color: var(--ink); text-decoration: none; font-size: 14px; font-weight: 500;
  opacity: 0.7; transition: opacity 0.2s, background 0.2s; cursor: pointer;
}
.landing-nav-scope .nav-link:hover { opacity: 1; }
/* Gray hover for the left-side tab options */
.landing-nav-scope .nav-tab {
  display: inline-flex; align-items: center; gap: 6px;
  padding: 7px 12px; border-radius: 8px;
  background: transparent; border: none; cursor: pointer;
  font-family: inherit; font-size: 14px; font-weight: 500;
  color: var(--ink); opacity: 0.7;
  transition: background 0.18s, opacity 0.18s;
}
.landing-nav-scope .nav-tab:hover { background: rgba(0,0,0,0.06); opacity: 1; }
.landing-nav-scope.dark-nav .nav-tab { color: #fff; }
.landing-nav-scope.dark-nav .nav-tab:hover { background: rgba(255,255,255,0.08); }
.landing-nav-scope.dark-nav .nav-link { color: #fff; }
.landing-nav-scope.dark-nav .features-dropdown {
  background: #1a1a1a;
  border-bottom: 1px solid rgba(255,255,255,0.12);
  box-shadow: 0 16px 48px rgba(0,0,0,0.7);
}
.landing-nav-scope.dark-nav .dropdown-feature-item { color: #f1f1f1; }
.landing-nav-scope.dark-nav .dropdown-feature-item:hover { background: rgba(255,255,255,0.08); }
.landing-nav-scope.dark-nav .dropdown-feature-item div { color: #f1f1f1 !important; }
.landing-nav-scope.dark-nav .dropdown-feature-item div + div { color: rgba(255,255,255,0.55) !important; }
.landing-nav-scope .btn-primary {
  background: var(--blue-dark); color: white; border: none; padding: 14px 28px;
  border-radius: 6px; font-size: 15px; font-weight: 600; cursor: pointer;
  transition: all 0.2s; font-family: inherit; letter-spacing: -0.01em;
}
.landing-nav-scope .btn-primary:hover {
  filter: brightness(1.08); transform: translateY(-1px);
  box-shadow: 0 8px 24px rgba(0,0,0,0.18);
}
.landing-nav-scope .features-dropdown {
  position: fixed; top: 64px; left: 0; right: 0; z-index: 95;
  background: rgba(255,255,255,0.97); backdrop-filter: blur(16px);
  -webkit-backdrop-filter: blur(16px); border-bottom: 1px solid var(--border);
  box-shadow: 0 16px 48px rgba(0,0,0,0.1); transform-origin: top center;
  transition: opacity 0.18s ease, transform 0.18s ease;
}
.landing-nav-scope .features-dropdown.open { opacity: 1; transform: scaleY(1); pointer-events: all; }
.landing-nav-scope .features-dropdown.closed { opacity: 0; transform: scaleY(0.96); pointer-events: none; }
@keyframes svd-item-in {
  from { opacity: 0; transform: translateX(-8px); }
  to   { opacity: 1; transform: translateX(0); }
}
.landing-nav-scope .features-dropdown.open .dropdown-feature-item {
  animation: svd-item-in 0.22s ease both;
}
.landing-nav-scope .dropdown-feature-item {
  display: flex; align-items: center; gap: 12px; padding: 10px 12px;
  border-radius: 10px; text-decoration: none; color: var(--ink);
  transition: background 0.15s; cursor: pointer;
}
.landing-nav-scope .dropdown-feature-item:hover { background: rgba(0,0,0,0.04); }
.landing-nav-scope .dropdown-feature-item:hover .dropdown-icon { color: var(--green-dark); }
.landing-nav-scope .dropdown-icon {
  width: 28px; height: 28px; background: transparent; color: var(--muted);
  display: flex; align-items: center; justify-content: center; font-size: 19px;
  line-height: 1; flex-shrink: 0; transition: color 0.15s;
}
.landing-nav-scope .nav-mobile-menu { display: none; }
.landing-nav-scope .mobile-menu-panel {
  position: fixed; top: 64px; left: 0; right: 0; bottom: 0;
  background: rgba(244,241,235,0.98); backdrop-filter: blur(12px); z-index: 90;
  padding: 24px; display: flex; flex-direction: column; gap: 8px; overflow-y: auto;
}
.landing-nav-scope .mobile-nav-link {
  display: block; padding: 14px 0; font-size: 20px; font-weight: 600;
  color: var(--ink); text-decoration: none; border-bottom: 1px solid var(--border);
}
@media (max-width: 900px) {
  .landing-nav-scope .nav-center { display: none !important; }
  .landing-nav-scope .nav-mobile-menu { display: flex !important; }
}
`;

type Lang = "da" | "en";

const NAV_T = {
  da: {
    features: "Funktioner", svendAi: "Svend AI", pricing: "Priser",
    revisor: "Revisor Svend" as string | null,
    dashboard: "Gå til dashboard →", login: "Log ind", tryFree: "Prøv gratis",
    seeAll: "Se alle funktioner →",
    featuresHref: "/features", aiHref: "/ai", howHref: "/#how", pricingHref: "/pricing", revisorHref: "/revisor",
    items: [
      { icon: "⚡", title: "Automatisering", desc: "Påmindelser og rykkere — sendt automatisk", href: "/features/automation" },
      { icon: "🧾", title: "Fakturascanning", desc: "Scan kvitteringer direkte ind på jobbet", href: "/features/invoice-scanning" },
      { icon: "📅", title: "Kalender", desc: "Uge- og månedsoversigt for hele holdet", href: "/features/calendar" },
      { icon: "⏱", title: "Tidsregistrering", desc: "Stempl ind og ud fra mobilen", href: "/features/time-registration" },
      { icon: "📸", title: "Kvalitets-dokumentation", desc: "Billeder, underskrift og kvalitetskontrol", href: "/features/quality-control" },
      { icon: "📄", title: "Tilbud", desc: "Opret og send professionelle tilbud", href: "/features/offer" },
      { icon: "📊", title: "Rapporter", desc: "Indsigt i timer, økonomi og eksport", href: "/features/reports" },
      { icon: "➕", title: "Og mere", desc: "Se hele funktionsoversigten", href: "/features" },
    ],
  },
  en: {
    features: "Features", svendAi: "Svend AI", pricing: "Pricing",
    revisor: null as string | null,
    dashboard: "Go to dashboard →", login: "Sign in", tryFree: "Try free",
    seeAll: "See all features →",
    featuresHref: "/en/features", aiHref: "/en/ai", howHref: "/en#how", pricingHref: "/en/pricing", revisorHref: "/revisor",
    items: [
      { icon: "⚡", title: "Automation", desc: "Reminders and chasers — sent automatically", href: "/en/features/automation" },
      { icon: "🧾", title: "Invoice scanning", desc: "Scan receipts directly onto the job", href: "/en/features/invoice-scanning" },
      { icon: "📅", title: "Calendar", desc: "Week and month view for the whole team", href: "/en/features/calendar" },
      { icon: "⏱", title: "Time registration", desc: "Clock in and out from your phone", href: "/en/features/time-registration" },
      { icon: "📸", title: "QC documentation", desc: "Photos, signature and quality control", href: "/en/features/quality-control" },
      { icon: "📄", title: "Offers", desc: "Create and send professional offers", href: "/en/features/offer" },
      { icon: "📊", title: "Reports", desc: "Insights on hours, finances and export", href: "/en/features/reports" },
      { icon: "➕", title: "And more", desc: "See the full feature overview", href: "/en/features" },
    ],
  },
} as const;

export default function LandingNav({ lang = "da", accent = "green", dark = false }: { lang?: Lang; accent?: "green" | "blue"; dark?: boolean }) {
  const accentColor = accent === "blue" ? "#2563EB" : dark ? "#fff" : "var(--green-dark)";
  const t = NAV_T[lang];
  const [scrollY, setScrollY] = useState(0);
  const [featuresOpen, setFeaturesOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const featuresMenuRef = useRef<HTMLDivElement | null>(null);
  const featuresDropdownRef = useRef<HTMLDivElement | null>(null);
  const { isSignedIn } = useAuth();
  const { organization } = useOrganization();
  const router = useRouter();

  useEffect(() => {
    if (isSignedIn && organization) {
      router.push(`/organization/${organization.id}`);
    } else if (isSignedIn) {
      router.push("/select-org");
    }
  }, [isSignedIn, organization, router]);

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const handleDocClick = (e: MouseEvent) => {
      const inTrigger = featuresMenuRef.current?.contains(e.target as Node);
      const inDropdown = featuresDropdownRef.current?.contains(e.target as Node);
      if (!inTrigger && !inDropdown) {
        setFeaturesOpen(false);
      }
    };
    document.addEventListener("mousedown", handleDocClick);
    return () => document.removeEventListener("mousedown", handleDocClick);
  }, []);

  useEffect(() => {
    if (scrollY > 20) setFeaturesOpen(false);
  }, [scrollY]);

  return (
    <div className={`landing-nav-scope ${navFontClass}${dark ? " dark-nav" : ""}`}>
      <style>{NAV_CSS}</style>
      <nav
        style={{
          position: "fixed", top: 0, left: 0, right: 0, zIndex: 100,
          height: 64,
          background: dark ? "#000" : "#FFFFFF",
          transition: "all 0.3s",
          borderBottom: "none",
        }}
      >
        <div style={{
          maxWidth: 1280,
          margin: "0 auto",
          padding: "0 clamp(12px, 2vw, 24px)",
          height: "100%",
          display: "flex", alignItems: "center", justifyContent: "space-between",
        }}>
        {/* Left: logo + nav tabs */}
        <div style={{ display: "flex", alignItems: "center", gap: 24, minWidth: 0 }}>
          {/* Logo */}
          <Link href={lang === "en" ? "/en" : "/"} style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0, textDecoration: "none", color: dark ? "#fff" : "var(--ink)" }}>
            <div style={{ width: 32, height: 32, borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden" }}>
              <img src="/sundok-icon.png" alt="Svendetid" style={{ width: 32, height: 32, objectFit: "contain" }} />
            </div>
            <span style={{ fontFamily: "var(--font-serif-display), serif", fontSize: 22, fontWeight: 400, letterSpacing: "-0.02em" }}>
              Svendetid
            </span>
          </Link>

          {/* Nav tabs — hidden on mobile */}
          <div className="nav-center" style={{ display: "flex", alignItems: "center", gap: 4, color: dark ? "#fff" : undefined }}>
            {/* Funktioner with dropdown */}
            <div ref={featuresMenuRef} style={{ position: "relative" }}>
              <button
                className="nav-tab"
                onClick={() => router.push(t.featuresHref)}
                onMouseEnter={() => setFeaturesOpen(true)}
                aria-expanded={featuresOpen}
              >
                <span>{t.features}</span>
                <svg viewBox="0 0 24 24" fill="none" style={{ width: 14, height: 14, color: "var(--muted)", transition: "transform 0.2s", transform: featuresOpen ? "rotate(180deg)" : "rotate(0deg)" }}>
                  <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
            </div>

            <Link href={t.aiHref} className="nav-tab" style={{ textDecoration: "none" }}>{t.svendAi}</Link>
            <Link href={t.pricingHref} className="nav-tab" style={{ textDecoration: "none" }}>{t.pricing}</Link>
            {t.revisor && <Link href={t.revisorHref} className="nav-tab" style={{ textDecoration: "none" }}>{t.revisor}</Link>}
          </div>
        </div>

        {/* Right */}
        <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
          {isSignedIn ? (
            <button className="btn-primary" onClick={() => router.push("/select-org")} style={{ padding: "10px 20px", fontSize: 14, background: accentColor }}>
              {t.dashboard}
            </button>
          ) : (
            <>
              <SignInButton mode="modal" fallbackRedirectUrl="/dashboard">
                <button className="nav-link nav-center" style={{ background: "transparent", border: "none", padding: 0, fontSize: 14, color: dark ? "rgba(255,255,255,0.7)" : undefined }}>
                  {t.login}
                </button>
              </SignInButton>
              <SignUpButton mode="modal" fallbackRedirectUrl="/org/create">
                <button className="btn-primary" style={{ padding: "10px 20px", fontSize: 14, background: dark ? "#fff" : "#000", color: dark ? "#000" : "#fff" }}>
                  {t.tryFree}
                </button>
              </SignUpButton>
            </>
          )}
          {/* Hamburger — mobile only */}
          <button className="nav-mobile-menu" onClick={() => setMobileOpen((s) => !s)} style={{ background: "transparent", border: "none", cursor: "pointer", padding: 4, gap: 4 }} aria-label="Menu">
            {mobileOpen ? (
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 6L6 18M6 6l12 12" /></svg>
            ) : (
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 12h18M3 6h18M3 18h18" /></svg>
            )}
          </button>
        </div>
        </div>
      </nav>

      {/* ===== FEATURES DROPDOWN ===== */}
      <div ref={featuresDropdownRef} className={`features-dropdown ${featuresOpen ? "open" : "closed"}`} onMouseLeave={() => setFeaturesOpen(false)}>
        <div style={{ maxWidth: 1100, margin: "0 auto", padding: "20px 40px 24px" }}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(8, 1fr)", gap: 4 }}>
            {t.items.map((item, i) => {
              const isMore = i === t.items.length - 1;
              return (
                <Link
                  key={item.title}
                  href={item.href}
                  className="dropdown-feature-item"
                  onClick={() => setFeaturesOpen(false)}
                  style={{
                    flexDirection: "column",
                    alignItems: "flex-start",
                    gap: 6,
                    padding: "12px 10px",
                    borderRadius: 10,
                    border: isMore ? `1.5px dashed ${dark ? "rgba(255,255,255,0.2)" : "var(--border)"}` : "1.5px solid transparent",
                    background: isMore ? "transparent" : undefined,
                    animationDelay: `${i * 28}ms`,
                  }}
                >
                  <span style={{ fontSize: 22, lineHeight: 1 }}>{item.icon}</span>
                  <div>
                    <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 2, color: isMore ? (dark ? "rgba(255,255,255,0.45)" : "var(--muted)") : (dark ? "#f1f1f1" : "var(--ink)") }}>{item.title}</div>
                    <div style={{ fontSize: 11, color: dark ? "rgba(255,255,255,0.5)" : "var(--muted)", lineHeight: 1.4 }}>{item.desc}</div>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </div>

      {/* Mobile menu panel */}
      {mobileOpen && (
        <div className="mobile-menu-panel">
          <Link href={t.featuresHref} className="mobile-nav-link" onClick={(e) => { e.preventDefault(); setMobileOpen(false); setTimeout(() => router.push(t.featuresHref), 10); }}>{t.features}</Link>
          <Link href={t.aiHref} className="mobile-nav-link" onClick={(e) => { e.preventDefault(); setMobileOpen(false); setTimeout(() => router.push(t.aiHref), 10); }}>{t.svendAi}</Link>
          <Link href={t.pricingHref} className="mobile-nav-link" onClick={(e) => { e.preventDefault(); setMobileOpen(false); setTimeout(() => router.push(t.pricingHref), 10); }}>{t.pricing}</Link>
          {t.revisor && <Link href={t.revisorHref} className="mobile-nav-link" onClick={(e) => { e.preventDefault(); setMobileOpen(false); setTimeout(() => router.push(t.revisorHref), 10); }}>{t.revisor}</Link>}

          {isSignedIn ? (
            <button className="btn-primary" onClick={() => router.push("/select-org")} style={{ width: "100%", padding: "16px", fontSize: 16, marginTop: 8 }}>
              {t.dashboard}
            </button>
          ) : (
            <>
              <SignInButton mode="modal" fallbackRedirectUrl="/dashboard">
                <button className="mobile-nav-link" style={{ background: "transparent", border: "none", textAlign: "left" }}>{t.login}</button>
              </SignInButton>
              <SignUpButton mode="modal" fallbackRedirectUrl="/org/create">
                <button className="btn-primary" style={{ width: "100%", padding: "16px", fontSize: 16, marginTop: 8 }}>{t.tryFree}</button>
              </SignUpButton>
            </>
          )}
        </div>
      )}
    </div>
  );
}
